import { FormControl, ValidationErrors, ValidatorFn, Validators, FormGroup, FormArray } from '@angular/forms';
import * as _ from 'lodash';
import { DVBaseAsyncValidator } from './base-async-dv';
import { Subscription, Subject, Observable, merge, of } from 'rxjs';

interface ControlsType {
  [name: string]: DVFormControl | DVFormGroup | DVFormArray;
}

export class DVFormGroup extends FormGroup {
  controls: ControlsType;
  private _blueprint: DVFormBlueprint;
  constructor(controls: ControlsType, bluePrint?: DVFormBlueprint) {
    if (bluePrint) {
      super(executeBlueprint(bluePrint, true));
    } else {
      super(controls);
    }
    if (bluePrint) {
      this._blueprint = bluePrint;
    }
  }

  error(field: string): string {
    return (this.controls[field] as any).errorMessage;
  }

  addError(field: string, errorName: string, errorMessage: string, force = false) {
    (this.controls[field] as DVFormControl).addError(errorName, errorMessage);
    if (force) {
      (this.controls[field] as DVFormControl).markAsTouched();
    }
  }

  addDynamicAsyncValidator(asyncdv: DVBaseAsyncValidator) {
    asyncdv.apply(this);
  }

  forceShowErrors() {
    if (this.invalid) {
      // window.scroll({
      //   top: 0,
      //   behavior: 'smooth'
      // });
    }
    for (const controlKey of Object.keys(this.controls)) {
      // tslint:disable-next-line: no-use-before-declare
      if (this.controls[controlKey] instanceof DVFormControl) {
        if ((this.controls[controlKey] as DVFormControl).forceErrorMessage) {
          this.controls[controlKey].markAsTouched();
        }
      // tslint:disable-next-line: no-use-before-declare
      } else if (this.controls[controlKey] instanceof DVFormGroup || this.controls[controlKey] instanceof DVFormArray) {
        // @ts-ignore
        this.controls[controlKey].forceShowErrors();
      }
    }
  }

  getCleanValue() {

    let output = this.getRawValue();
    const clean = (object) => {
      Object
        .entries(object)
        .forEach(([s, v]) => {
          const k: any = s;
          if (v && typeof v === 'object') {
            clean(v);
          }
          if (v && typeof v === 'object' && !Object.keys(v).length || v === null || v === undefined || v === '' || v === {} || v === []) {
            if (Array.isArray(object)) {
              object.splice(k, 1);
            } else {
              delete object[k];
            }
          }
        });
      return object;
    };
    output = clean(output);
    return output;

  }

  resetToDefault() {
    this.resetToDefaultExcept();
  }

  resetToDefaultExcept(keys: Array<string> = []) {
    for (const controlName in this.controls) {
      if (keys.includes(controlName)) {
        continue;
      }
      const control = this.controls[controlName];
      // tslint:disable-next-line: no-use-before-declare
      if (control instanceof DVFormControl) {
        control.resetToDefault();
      // tslint:disable-next-line: no-use-before-declare
      } else if (control instanceof DVFormArray) {
        control.resetToDefault();
      } else if (control instanceof DVFormGroup) {
        control.resetToDefault();
      }
    }
  }
}

export class DVFormControl extends FormControl {
  errorMessages: ErrorMessage[];
  defaultValue: any = null;

  statusChanges$: Observable<any>;
  touchedChanges: Subject<boolean> = new Subject<boolean>();

  constructor(defaultValue: any = null, validators: DValidators = {}) {
    super(defaultValue);
    this.addValidators(validators);
    if (defaultValue !== null) {
      this.defaultValue = defaultValue;
    }

    this.statusChanges$ = merge(
      this.statusChanges,
      this.touchedChanges
    );
  }
  markAsTouched({ onlySelf }: { onlySelf?: boolean } = {}): void {
    super.markAsTouched({ onlySelf });
    this.touchedChanges.next(true);
  }

  markAsUntouched({ onlySelf }: { onlySelf?: boolean } = {}): void {
    super.markAsUntouched({ onlySelf });
    this.touchedChanges.next(false);
  }

  resetToDefault() {
    this.setValue(this.defaultValue);
  }

  clearValidators() {
    this.errorMessages = [];
    this.setValidators(null);
    this.updateValueAndValidity();
  }

  addValidators(validators: DValidators) {
    const validatorArray: ValidatorFn[] = [];
    const errorMessages: ErrorMessage[] = [];
    Object.keys(validators).forEach((message: string) => {
      validatorArray.push(validators[message]);
      errorMessages.push({
        errorFunction: validators[message],
        errorMessage: message,
      });
    });
    this.setValidators(validatorArray);
    this.errorMessages = errorMessages;
  }

  addError(errorName: string, message: string) {
    this.errorMessages.push({
      errorMessage: message,
      asyncError: true,
      errorName
    });
    this.setErrors({ [`${errorName}`]: true });
  }

  get errorMessage() {
    if (this.touched && this.invalid) {
      const currentErrorkey = Object.keys(this.errors)[0];
      return this.getErrorMessageByKey(currentErrorkey);
    } else {
      return null;
    }
  }

  private getErrorMessageByKey(key) {
    if (this.errorMessages.findIndex(msgObj => msgObj.errorName === key) >= 0) {
      return this.errorMessages.find(msgObj => msgObj.errorName === key).errorMessage;
    }
    for (const msgObj of this.errorMessages) {
      const error = msgObj.errorFunction(this);
      if (error && Object.keys(error)[0] === key) {
        return msgObj.errorMessage;
      }
    }
    return null;
  }

  get forceErrorMessage() {
    if (this.invalid) {
      const validations = this.validator(this);
      if (!validations) {
        return null;
      }
      const currentErrorkey = Object.keys(validations)[0];
      return this.getErrorMessageByKey(currentErrorkey);
    } else {
      return null;
    }
  }

}

export class DVFormArray extends FormArray {
  onPatchValue = new Subject();
  controls: Array<DVFormArray | DVFormControl | DVFormGroup>;
  errorMessages: ErrorMessage[];

  private _blueprint: DVFormBlueprint;
  private _initialRepeat: number;
  private _initialized = false;
  constructor(
    array: Array<DVFormControl> | Array<DVFormGroup>,
    blueprint?: DVFormBlueprint,
    initialRepeat = 0,
    validators: DValidators = {}
  ) {
    super(array);
    this.addValidators(validators);
    if (blueprint) {
      this._blueprint = blueprint;
    }
    this._initialRepeat = initialRepeat;
  }
  init() {
    if (this.hasBlueprint() && this._initialRepeat > 0 && this._initialized) {
      this.addChildrenFromBlueprint();
      this._initialized = true;
    }
  }

  patchValue(value: any[], options?: {
    onlySelf?: boolean,
    emitEvent?: boolean
  }) {
    if (this.hasBlueprint()) {
      const newLength = value.length;
      const currentLength = this.length;
      if (newLength > currentLength) {
        const diff = newLength - currentLength;
        for (let i = 0; i < diff; i++) {
          this.addChildrenFromBlueprint();
        }
      } else if (currentLength > newLength) {
        const diff = currentLength - newLength;
        for (let i = 0; i < diff; i++) {
          this.removeAt(this.length - i - 1);
        }
      }
    }
    super.patchValue(value, options);
  }


  addChildrenToArray(child: DVFormControl | DVFormGroup | DVFormArray) {
    this.controls.push(child);
    child.setParent(this);
  }

  hasBlueprint() {
    return !!this._blueprint;
  }

  getBlueprint() {
    return this._blueprint;
  }

  addChildrenFromBlueprint(bluePrint?: DVFormBlueprint) {
    if (bluePrint) {
      // console.log('blue print provided in params')
      const group = executeBlueprint(bluePrint);
      this.addChildrenToArray(group);
    } else if (this.hasBlueprint()) {
      // console.log('array has blueprint');
      // const group = executeBlueprint(this._blueprint);
      // console.log('generated group', group);
      this.addChildrenToArray(executeBlueprint(this._blueprint));
      // this.push(group);
      // this.controls.push(group)
      // group.setParent(this);
    } else {
      throw new Error('DV Form Array does NOT contain a blueprint');
    }
  }

  resetToDefault() {
    for (const control of this.controls) {
      if (control instanceof DVFormControl) {
        control.resetToDefault();
      } else if (control instanceof DVFormArray) {
        control.resetToDefault();
      } else if (control instanceof DVFormGroup) {
        control.resetToDefault();
      }
    }
  }

  forceShowErrors() {
    if (this.invalid) {
      this.markAsTouched();
    }
    for (const control of this.controls) {
      if (control instanceof DVFormControl) {
        if ((control as DVFormControl).forceErrorMessage) {
          control.markAsTouched();
        }
      } else if (control instanceof DVFormArray || control instanceof DVFormGroup) {
        control.forceShowErrors();
      }
    }
  }

  clearValidators() {
    this.errorMessages = [];
    this.setValidators(null);
    this.updateValueAndValidity();
  }

  addValidators(validators: DValidators) {
    const validatorArray: ValidatorFn[] = [];
    const errorMessages: ErrorMessage[] = [];
    Object.keys(validators).forEach((message: string) => {
      validatorArray.push(validators[message]);
      errorMessages.push({
        errorFunction: validators[message],
        errorMessage: message,
      });
    });
    this.setValidators(validatorArray);
    this.errorMessages = errorMessages;
  }

  addError(errorName: string, message: string) {
    this.errorMessages.push({
      errorMessage: message,
      asyncError: true,
      errorName
    });
    this.setErrors({ [`${errorName}`]: true });
  }

  get errorMessage() {
    if (this.touched && this.invalid && this.errors) {
      const currentErrorkey = Object.keys(this.errors)[0];
      return this.getErrorMessageByKey(currentErrorkey);
    } else {
      return null;
    }
  }

  private getErrorMessageByKey(key) {
    if (this.errorMessages.findIndex(msgObj => msgObj.errorName === key) >= 0) {
      return this.errorMessages.find(msgObj => msgObj.errorName === key).errorMessage;
    }
    for (const msgObj of this.errorMessages) {
      const error = msgObj.errorFunction(this);
      if (error && Object.keys(error)[0] === key) {
        return msgObj.errorMessage;
      }
    }
    return null;
  }

  get forceErrorMessage() {
    if (this.invalid) {
      const validations = this.validator(this);
      if (!validations) {
        return null;
      }
      const currentErrorkey = Object.keys(validations)[0];
      return this.getErrorMessageByKey(currentErrorkey);
    } else {
      return null;
    }
  }
}

export function executeBlueprint(blueprint: DVFormBlueprint, raw = false) {
  const obj = {} as any;
  for (const item of blueprint) {
    switch (item.type) {
      case 'ARRAY':
        obj[item.name] = new DVFormArray(
          item.default || null,
          item.blueprint,
          item.initialRepeat || 0
        );
        break;
      case 'CONTROL':
        obj[item.name] = new DVFormControl(
          item.default || null,
          item.validators
        );
        break;
      case 'GROUP':
        obj[item.name] = new DVFormGroup({}, item.blueprint);
        break;
    }
  }
  if (raw) {
    return obj;
  } else {
    return new DVFormGroup(obj);
  }
}

interface DValidators {
  [message: string]: ValidatorFn;
}

interface ErrorMessage {
  errorFunction?: ValidatorFn;
  errorMessage: string;
  asyncError?: boolean;
  errorName?: string;
}

interface DVFormBlueprintItem {
  name: string;
  type: 'GROUP' | 'ARRAY' | 'CONTROL';
  default: any;
  validators?: DValidators;
  blueprint?: DVFormBlueprint;
  initialRepeat?: number;
}

type DVFormBlueprint = Array<DVFormBlueprintItem>;
